// Thin client for Deep Research backend edge functions.
// Each helper returns a typed result, and reports `degraded` when the function
// answered 428 (missing secret) so the engine can fall back gracefully.

import { supabase } from "@/integrations/supabase/client";

export type Provider =
  | "openai" | "anthropic" | "gemini" | "groq" | "mistral"
  | "cohere" | "perplexity" | "deepseek" | "xai" | "openrouter" | "lovable";

export interface LLMCallArgs {
  provider: Provider;
  model: string;
  messages: Array<{ role: "system" | "user" | "assistant"; content: string }>;
  temperature?: number;
  max_tokens?: number;
  json?: boolean;
}

export interface LLMCallResult {
  content: string;
  tokens_in: number;
  tokens_out: number;
  latency_ms: number;
  cost_usd: number;
  provider: string;
  model: string;
}

export interface SearchHit {
  url: string;
  title: string;
  snippet: string;
  published_at?: string;
  score: number;
}

export interface CallReport<T> {
  ok: true; data: T;
}
export interface FailReport {
  ok: false; status: number; error: string; detail?: string; secret?: string;
}
export type ApiResult<T> = CallReport<T> | FailReport;

async function invoke<T>(name: string, body: unknown): Promise<ApiResult<T>> {
  try {
    const { data, error } = await supabase.functions.invoke(name, { body });
    if (error) {
      const ctx = (error as { context?: { status?: number } }).context;
      const status = ctx?.status ?? 500;
      const detail = (data as { error?: string; detail?: string; secret?: string } | undefined);
      return {
        ok: false, status,
        error: detail?.error ?? error.message ?? "edge_failed",
        detail: detail?.detail, secret: detail?.secret,
      };
    }
    return { ok: true, data: data as T };
  } catch (e) {
    return { ok: false, status: 0, error: "network_error", detail: e instanceof Error ? e.message : String(e) };
  }
}

export const llmCall = (args: LLMCallArgs) => invoke<LLMCallResult>("llm-call", args);

export const webSearch = (args: { query: string; max_results?: number; provider?: string }) =>
  invoke<{ provider: string; results: SearchHit[]; latency_ms: number }>("web-search", args);

export const embedTexts = (args: { texts: string[]; provider?: string; model?: string }) =>
  invoke<{ provider: string; embeddings: number[][]; dim: number; latency_ms: number }>("embed", args);

export const vectorSearch = (args: { embedding: number[]; session_id: string; top_k?: number; threshold?: number }) =>
  invoke<{ matches: Array<{ id: string; url: string; title: string; snippet: string; similarity: number }> }>(
    "vector-search", args,
  );

export const hashAudit = (args: { step: string; inputs?: unknown; outputs?: unknown; ts: number }) =>
  invoke<{ hash: string }>("hash-audit", args);

export interface BackendStatus {
  llm: { live: Provider[]; missing: Provider[] };
  search: { live: string[]; missing: string[] };
  embed: { live: string[]; missing: string[] };
  level: "LIVE" | "DEGRADED" | "MOCK";
}

const LLM_PROVIDERS: Provider[] = [
  "lovable", "openai", "anthropic", "gemini", "groq", "mistral",
  "cohere", "perplexity", "deepseek", "xai", "openrouter",
];

export async function probeBackend(): Promise<BackendStatus> {
  const status: BackendStatus = {
    llm: { live: [], missing: [] },
    search: { live: [], missing: ["TAVILY_API_KEY", "EXA_API_KEY", "BRAVE_SEARCH_API_KEY", "SERPAPI_API_KEY"] },
    embed: { live: [], missing: ["OPENAI_API_KEY", "VOYAGE_API_KEY", "COHERE_API_KEY", "GOOGLE_GEMINI_API_KEY"] },
    level: "MOCK",
  };

  const s = await webSearch({ query: "ping", max_results: 1 });
  if (s.ok) {
    status.search.live = [s.data.provider];
    status.search.missing = [];
  }

  const e = await embedTexts({ texts: ["ping"] });
  if (e.ok) {
    status.embed.live = [e.data.provider];
    status.embed.missing = [];
  }

  const l = await llmCall({
    provider: "lovable",
    model: "google/gemini-2.5-flash-lite",
    messages: [{ role: "user", content: "ping" }],
    max_tokens: 1,
  });
  if (l.ok) status.llm.live.push("lovable");

  status.llm.missing = LLM_PROVIDERS.filter((p) => !status.llm.live.includes(p));

  if (status.llm.live.length && status.search.live.length) status.level = "LIVE";
  else if (status.llm.live.length || status.search.live.length) status.level = "DEGRADED";
  else status.level = "MOCK";
  return status;
}
